/**
 * − and + beside each quantity on the cart page, and the cart updated as
 * soon as a quantity changes.
 *
 * The classic cart draws a bare number box, where the product page has
 * WooCommerce's stepper; a box whose arrows show only on hover, and not at
 * all on a phone, read as fixed. The buttons here change the number within
 * the box's own min, max and step.
 *
 * And a changed quantity -- by the buttons or typed -- updates the cart by
 * itself, a moment after the last change, so several clicks are one update.
 * It does what "Päivitä ostoskori" does, through WooCommerce's own cart
 * script; with nothing left for it to do, that button is hidden. Without
 * this script the button is there and the cart works as it always has.
 *
 * WooCommerce redraws the cart table after an update (updated_wc_div), so the
 * buttons go back on each time. A line held at one has a hidden box, and gets
 * none. The labels come from lib/woocommerce.php (window.diakonStore).
 */

const FORM = 'form.woocommerce-cart-form';
const DONE = 'diakon-stepper';
const AUTO = 'diakon-cart-autoupdate';
const WAIT = 700;

let pending = null;

/**
 * Updates the cart a moment after the last change: WooCommerce's update
 * button, switched on as a change switches it on, and clicked. Its cart
 * script sends the form and redraws the table.
 */
function scheduleUpdate() {
	window.clearTimeout(pending);
	pending = window.setTimeout(() => {
		const update = document.querySelector(
			`${FORM} button[name="update_cart"]`
		);

		if (update) {
			update.disabled = false;
			update.removeAttribute('aria-disabled');
			update.click();
		}
	}, WAIT);
}

/**
 * One button: − or +.
 *
 * @param {HTMLInputElement} input     The quantity box.
 * @param {number}           direction -1 or 1.
 * @param {string}           label     Its accessible name.
 * @return {HTMLButtonElement} The button.
 */
function stepButton(input, direction, label) {
	const button = document.createElement('button');
	button.type = 'button';
	button.className = `diakon-stepper__button diakon-stepper__button--${
		direction < 0 ? 'minus' : 'plus'
	}`;
	button.textContent = direction < 0 ? '−' : '+';
	button.setAttribute('aria-label', label);

	button.addEventListener('click', () => {
		const step = parseFloat(input.step) || 1;
		const min = input.min === '' ? 0 : parseFloat(input.min);
		const max = input.max === '' ? Infinity : parseFloat(input.max);
		const value = (parseFloat(input.value) || 0) + direction * step;

		input.value = String(Math.min(max, Math.max(min, value)));

		// WooCommerce's cart script listens with jQuery.
		if (window.jQuery) {
			window.jQuery(input).trigger('change');
		} else {
			input.dispatchEvent(new Event('change', { bubbles: true }));
		}

		scheduleUpdate();
	});

	return button;
}

function addSteppers() {
	const labels = window.diakonStore?.quantityLabels;

	if (!labels) {
		return;
	}

	document
		.querySelectorAll(`${FORM} .quantity:not(.${DONE})`)
		.forEach((quantity) => {
			const input = quantity.querySelector(
				'input.qty:not([type="hidden"])'
			);

			if (!input || input.readOnly) {
				return;
			}

			quantity.classList.add(DONE);
			input.before(stepButton(input, -1, labels.decrease));
			input.after(stepButton(input, 1, labels.increase));
		});
}

export function initCartQuantityStepper() {
	if (!document.querySelector(FORM)) {
		return;
	}

	addSteppers();
	document.body.classList.add(AUTO);

	// A quantity typed in: once the box is left or Enter pressed (change),
	// or after a pause in typing a number.
	document.addEventListener('change', (event) => {
		if (event.target.matches(`${FORM} input.qty`)) {
			scheduleUpdate();
		}
	});

	document.addEventListener('input', (event) => {
		if (
			event.target.matches(`${FORM} input.qty`) &&
			event.target.value !== ''
		) {
			scheduleUpdate();
		}
	});

	if (window.jQuery) {
		window.jQuery(document.body).on('updated_wc_div', addSteppers);
	}
}
